/**
 * Foremann Voice Assistant Component
 *
 * A floating voice assistant that allows hands-free order management.
 * Uses Web Speech API for voice recognition and Gemini for NLU.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Mic,
  MicOff,
  X,
  MessageSquare,
  Volume2,
  VolumeX,
  Loader2,
  Bot,
  User,
  CheckCircle,
  AlertCircle,
  Minimize2,
  Maximize2,
} from 'lucide-react';
import { foremannAI, type ConversationMessage, type ForemannState } from '../services/foremannAI';
import { orderApi } from '../services/api';
import type { Order } from '../types';

interface ForemannVoiceAssistantProps {
  onOrderCreated?: (order: Order) => void;
}

export function ForemannVoiceAssistant({ onOrderCreated }: ForemannVoiceAssistantProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [state, setState] = useState<ForemannState>({
    isListening: false,
    isProcessing: false,
    isSpeaking: false,
    currentIntent: null,
    pendingOrder: {},
    conversationHistory: [],
  });
  const [error, setError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [inputText, setInputText] = useState('');

  useEffect(() => {
    // Set up state callback
    foremannAI.setStateCallback((newState) => {
      setState((prev) => ({ ...prev, ...newState }));
    });
  }, []);

  const handleVoiceInput = useCallback(async () => {
    setError(null);
    try {
      const text = await foremannAI.startListening();
      if (text) {
        await processCommand(text);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Villa við raddgreiningnu');
    }
  }, []);

  const processCommand = async (text: string) => {
    try {
      const result = await foremannAI.processCommand(text);

      // Handle different actions
      if (result.action === 'execute' && result.entities?.confirmation === 'yes') {
        // Create the order
        if (state.pendingOrder.customer_id) {
          const order = await orderApi.create({
            customer_id: state.pendingOrder.customer_id,
            container_id: state.pendingOrder.container_id,
            type: (state.pendingOrder.type || 'pickup') as Order['type'],
            scheduled_date: state.pendingOrder.scheduled_date,
            notes: state.pendingOrder.notes,
            status: 'pending',
          });

          if (onOrderCreated) {
            onOrderCreated(order.data);
          }

          setState((prev) => ({ ...prev, pendingOrder: {} }));
        }
      }

      // Update pending order with extracted entities
      if (result.intent === 'create_order' && result.entities) {
        setState((prev) => ({
          ...prev,
          pendingOrder: {
            ...prev.pendingOrder,
            ...result.entities,
          },
        }));
      }

      // Speak response if not muted
      if (!isMuted && result.response) {
        await foremannAI.speak(result.response);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Villa við vinnslu');
    }
  };

  const handleTextSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inputText.trim()) {
      await processCommand(inputText);
      setInputText('');
    }
  };

  const stopListening = () => {
    foremannAI.stopListening();
  };

  const clearHistory = () => {
    foremannAI.clearHistory();
    setState((prev) => ({ ...prev, conversationHistory: [], pendingOrder: {} }));
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
        title="Foremann AI Aðstoðarmaður"
      >
        <Bot className="h-7 w-7" />
      </button>
    );
  }

  return (
    <div
      className={`fixed z-50 bg-white rounded-2xl shadow-2xl transition-all ${
        isMinimized
          ? 'bottom-6 right-6 w-80 h-16'
          : 'bottom-6 right-6 w-96 max-h-[600px] flex flex-col'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-2xl">
        <div className="flex items-center gap-2 text-white">
          <Bot className="h-5 w-5" />
          <span className="font-semibold">Foremann</span>
          {state.isListening && (
            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
              Hlustandi...
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMuted(!isMuted)}
            className="p-1.5 rounded-lg hover:bg-white/20 text-white"
            title={isMuted ? 'Kveikja á hljóði' : 'Slökkva á hljóði'}
          >
            {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1.5 rounded-lg hover:bg-white/20 text-white"
          >
            {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 rounded-lg hover:bg-white/20 text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Conversation */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px] max-h-[350px]">
            {(!state.conversationHistory || state.conversationHistory.length === 0) ? (
              <div className="text-center text-gray-500 py-8">
                <Bot className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">Hæ! Ég er Foremann.</p>
                <p className="text-sm mt-1">Segðu "tæming" til að stofna pöntun</p>
                <p className="text-xs mt-2 text-gray-400">Eða ýttu á hljóðnemann til að tala</p>
              </div>
            ) : (
              (state.conversationHistory || []).map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-white" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white rounded-br-md'
                        : 'bg-gray-100 text-gray-800 rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm">{msg.content}</p>
                  </div>
                  {msg.role === 'user' && (
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center">
                      <User className="h-4 w-4 text-gray-600" />
                    </div>
                  )}
                </div>
              ))
            )}

            {/* Processing indicator */}
            {state.isProcessing && (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Vinnur...</span>
              </div>
            )}

            {/* Speaking indicator */}
            {state.isSpeaking && (
              <div className="flex items-center gap-2 text-purple-600">
                <Volume2 className="h-4 w-4 animate-pulse" />
                <span className="text-sm">Talar...</span>
              </div>
            )}
          </div>

          {/* Pending Order Preview */}
          {Object.keys(state.pendingOrder).length > 0 && (
            <div className="mx-4 mb-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 text-blue-800 text-sm font-medium mb-2">
                <MessageSquare className="h-4 w-4" />
                Pöntun í vinnslu
              </div>
              <div className="text-xs text-blue-700 space-y-1">
                {state.pendingOrder.type && <p>Tegund: {state.pendingOrder.type}</p>}
                {state.pendingOrder.customer_name && <p>Viðskiptavinur: {state.pendingOrder.customer_name}</p>}
                {state.pendingOrder.scheduled_date && <p>Dagsetning: {state.pendingOrder.scheduled_date}</p>}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mx-4 mb-2 p-3 bg-red-50 rounded-lg border border-red-200 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Input Area */}
          <div className="p-4 border-t bg-gray-50 rounded-b-2xl">
            <div className="flex items-center gap-2">
              {/* Voice Button */}
              <button
                onClick={state.isListening ? stopListening : handleVoiceInput}
                disabled={state.isProcessing}
                className={`flex-shrink-0 p-3 rounded-full transition-all ${
                  state.isListening
                    ? 'bg-red-500 text-white animate-pulse'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                } disabled:opacity-50`}
                title={state.isListening ? 'Stöðva' : 'Tala'}
              >
                {state.isListening ? (
                  <MicOff className="h-5 w-5" />
                ) : (
                  <Mic className="h-5 w-5" />
                )}
              </button>

              {/* Text Input */}
              <form onSubmit={handleTextSubmit} className="flex-1 flex gap-2">
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Eða skrifaðu hér..."
                  className="flex-1 px-4 py-2 rounded-full border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  disabled={state.isProcessing || state.isListening}
                />
                <button
                  type="submit"
                  disabled={!inputText.trim() || state.isProcessing}
                  className="px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Senda
                </button>
              </form>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center justify-between mt-3">
              <div className="flex gap-1">
                <button
                  onClick={() => processCommand('tæming')}
                  className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded-full"
                  disabled={state.isProcessing}
                >
                  Tæming
                </button>
                <button
                  onClick={() => processCommand('afhending')}
                  className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded-full"
                  disabled={state.isProcessing}
                >
                  Afhending
                </button>
                <button
                  onClick={() => processCommand('hjálp')}
                  className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded-full"
                  disabled={state.isProcessing}
                >
                  Hjálp
                </button>
              </div>
              <button
                onClick={clearHistory}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Hreinsa
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ForemannVoiceAssistant;
